import { createSlice, createSelector } from '@reduxjs/toolkit';

const commentsSlice = createSlice({
  name: 'comments',
  initialState: {
    // Structure: { commentId: { full comment object with like data merged } }
    comments: {},
    // Structure: { postId: commentId[] } - ordered list of comment IDs per post
    commentLists: {},
    // Structure: { commentId: { expanded: bool, replies: [], loading: bool, hasMore: bool, page: number } }
    threads: {}
  },
  reducers: {
    // Initialize comment with full data when comment is first loaded
    initializeComment: (state, action) => {
      const comment = action.payload;
      
      // ONLY update if comment doesn't exist yet (prevent overwriting good data)
      if (!state.comments[comment.id]) {
        state.comments[comment.id] = {
          ...comment,
          // Ensure like data is properly set
          likeCount: comment.likesCount || 0,
          likedByMe: comment.likedByMe || false
        };
      } else {
        console.log('🛡️ Prevented overwriting existing comment data for:', comment.id?.slice(-8));
      }
    },

    // Optimistic update when user clicks like
    optimisticLikeToggle: (state, action) => {
      const { commentId } = action.payload;
      const comment = state.comments[commentId];
      if (comment) {
        // Only update like data, preserve all other comment properties
        comment.likedByMe = !comment.likedByMe;
        comment.likeCount = (comment.likeCount || 0) + (comment.likedByMe ? 1 : -1);
        comment.likesCount = comment.likeCount; // Keep both fields in sync
      }
    },

    // Update from WebSocket - updates count but preserves personal likedByMe state
    updateFromWebSocket: (state, action) => {
      const { commentId, likeCount } = action.payload;
      if (state.comments[commentId]) {
        // Only update the count, preserve all other comment properties including user data
        state.comments[commentId].likeCount = likeCount;
        state.comments[commentId].likesCount = likeCount; // Keep both fields in sync
      } else {
        // Initialize comment if it doesn't exist in Redux (shouldn't happen with new structure)
        console.warn('WebSocket update for unknown comment:', commentId);
      }
    },

    // Batch initialize multiple comments (for when comments are loaded)
    initializeComments: (state, action) => {
      const comments = action.payload;
      
      comments.forEach(comment => {
        
        state.comments[comment.id] = {
          ...comment,
          // Ensure like data is properly set
          likeCount: comment.likesCount || 0,
          likedByMe: comment.likedByMe || false
        };
      });
    },

    // Initialize comment list for a post
    initializeCommentList: (state, action) => {
      const { postId, commentIds } = action.payload;
      state.commentLists[postId] = commentIds;
    },

    // Add new comment to the beginning of a post's comment list
    addCommentToList: (state, action) => {
      const { postId, commentId } = action.payload;
      
      if (!state.commentLists[postId]) {
        state.commentLists[postId] = [];
      }
      state.commentLists[postId].unshift(commentId);
    },

    // Atomic action to add both comment data and list entry
    addNewComment: (state, action) => {
      const { postId, comment } = action.payload;
      
      // Initialize comment data
      state.comments[comment.id] = {
        ...comment,
        likeCount: comment.likesCount || 0,
        likedByMe: comment.likedByMe || false
      };
      
      // Add to comment list
      if (!state.commentLists[postId]) {
        state.commentLists[postId] = [];
      }
      state.commentLists[postId].unshift(comment.id);
    },

    // Thread actions for Instagram-style replies
    toggleThread: (state, action) => {
      const { commentId } = action.payload;
      if (!state.threads[commentId]) {
        state.threads[commentId] = { expanded: true, replies: [], loading: false, hasMore: true, page: 0 };
      } else {
        state.threads[commentId].expanded = !state.threads[commentId].expanded;
      }
    },

    loadRepliesStart: (state, action) => {
      const { commentId } = action.payload;
      if (!state.threads[commentId]) {
        state.threads[commentId] = { expanded: true, replies: [], loading: true, hasMore: true, page: 0 };
      } else {
        state.threads[commentId].loading = true;
      }
    },

    loadRepliesSuccess: (state, action) => {
      const { commentId, replies, hasMore, page } = action.payload;
      if (!state.threads[commentId]) {
        state.threads[commentId] = { expanded: true, replies: [], loading: false, hasMore: true, page: 0 };
      }
      
      const thread = state.threads[commentId];
      thread.loading = false;
      thread.hasMore = hasMore;
      thread.page = page;
      thread.expanded = true;
      
      // Also add replies to comments store for like/data management
      replies.forEach(reply => {
        state.comments[reply.id] = {
          ...reply,
          likeCount: reply.likesCount || 0,
          likedByMe: reply.likedByMe || false
        };
      });
      
      // Add reply IDs to thread (append for pagination, replace for first load)
      if (page === 0) {
        thread.replies = replies.map(r => r.id);
      } else {
        const newIds = replies.map(r => r.id);
        thread.replies = [...thread.replies, ...newIds];
      }
    },

    loadRepliesFailure: (state, action) => {
      const { commentId } = action.payload;
      if (state.threads[commentId]) {
        state.threads[commentId].loading = false;
      }
    },

    // Add a reply to a thread (for real-time/optimistic updates)
    addReplyToThread: (state, action) => {
      const { parentCommentId, reply } = action.payload;
      
      // Add reply to comments store
      state.comments[reply.id] = {
        ...reply,
        likeCount: reply.likesCount || 0,
        likedByMe: reply.likedByMe || false
      };
      
      // Add to thread if it exists
      if (state.threads[parentCommentId]) {
        state.threads[parentCommentId].replies.push(reply.id);
      }
      
      // Increment parent's reply count
      if (state.comments[parentCommentId]) {
        state.comments[parentCommentId].replyCount = (state.comments[parentCommentId].replyCount || 0) + 1;
      }
    },

    // Increment reply count for a comment
    incrementReplyCount: (state, action) => {
      const { commentId } = action.payload;
      if (state.comments[commentId]) {
        state.comments[commentId].replyCount = (state.comments[commentId].replyCount || 0) + 1;
      }
    }
  }
});

export const { 
  initializeComment, 
  optimisticLikeToggle, 
  updateFromWebSocket,
  initializeComments,
  initializeCommentList,
  addCommentToList,
  addNewComment,
  toggleThread,
  loadRepliesStart,
  loadRepliesSuccess,
  loadRepliesFailure,
  addReplyToThread,
  incrementReplyCount
} = commentsSlice.actions;

// Base selectors
const selectComments = (state) => state.comments.comments;
const selectCommentLists = (state) => state.comments.commentLists;
const selectThreads = (state) => state.comments.threads;

// Memoized selectors
export const selectCommentLikeData = createSelector(
  [selectComments, (state, commentId) => commentId],
  (comments, commentId) => {
    const comment = comments[commentId];
    return comment ? { 
      likeCount: comment.likeCount || 0, 
      likedByMe: comment.likedByMe || false 
    } : { likeCount: 0, likedByMe: false };
  }
);

export const selectCommentList = createSelector(
  [selectCommentLists, (state, postId) => postId],
  (commentLists, postId) => commentLists[postId] || []
);

// Selector to get full comment objects for a post (combining IDs with comment data)
export const selectPostComments = createSelector(
  [selectCommentList, selectComments, (state, postId) => postId],
  (commentIds, commentsData, postId) => {
    // Return empty array if no comment IDs for this post
    if (!commentIds || commentIds.length === 0) return [];
    
    // Map comment IDs to full comment objects from Redux store
    return commentIds
      .map(commentId => commentsData[commentId])
      .filter(comment => comment); // Filter out any undefined comments
  }
);

// Thread selectors
export const selectThreadState = createSelector(
  [selectThreads, (state, commentId) => commentId],
  (threads, commentId) => threads[commentId] || { expanded: false, replies: [], loading: false, hasMore: true, page: 0 }
);

export const selectThreadReplies = createSelector(
  [selectThreads, selectComments, (state, commentId) => commentId],
  (threads, comments, commentId) => {
    const thread = threads[commentId];
    if (!thread || !thread.replies) return [];
    return thread.replies.map(id => comments[id]).filter(Boolean);
  }
);

// Get a single comment by ID
export const selectCommentById = createSelector(
  [selectComments, (state, commentId) => commentId],
  (comments, commentId) => comments[commentId] || null
);

export default commentsSlice.reducer;